/**
 * HubTestHistoryItem — 학습(/students-hub) 전용 테스트 카드 (공유 TestHistoryItem 로컬 fork)
 *
 * 이원화 원칙: 공유 TestHistoryItem 은 무수정. 헤더(과목명 + 부제, 생성/수정일)와 상태 아이콘
 * 클릭(결과 상세보기) 동작은 원본과 동일하게 두고, collapsed=true(비선택 카드)일 때만 하단
 * 4열 grid 를 한 줄 텍스트(TID·난이도·문제수·정답률, (정/오/총) 브레이크다운 제외)로 축소해
 * 카드 세로 높이를 줄인다. collapsed=false(선택 카드)는 원본 grid 를 그대로 렌더한다.
 */

import { forwardRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { CheckCircle2, PlayCircle, FilePlus } from 'lucide-react';
import V2DateTime from '../common/V2DateTime';

/** 시험 종류 기호 — 오답노트 시험 카드(HubWrongNoteTestCard)도 같은 기호를 쓴다 */
export const examTypeSymbol = (t) =>
  t === 'alpha' ? 'α' :
  t === 'beta' ? 'β' :
  t === 'gamma' ? 'γ' :
  t === 'delta' ? 'δ' :
  t === 'custom' ? '𝐂' :
  t === 'epsilon' ? 'ε' :
  (t || '-');

const HubTestHistoryItem = forwardRef(({
  test,
  isSelected,
  onClick,
  compact = false,
  collapsed = false,
}, ref) => {
  const navigate = useNavigate();
  const { studentId } = useParams(); // URL에서 studentId 가져오기

  // compact: 세로형 패널에서 카드 세로 높이를 줄인다 (원본과 동일)
  const c = {
    pad: compact ? 'p-2.5' : 'p-[14.4px]',
    round: compact ? 'rounded-2xl' : 'rounded-3xl',
    shadow: compact ? 'shadow-sm' : 'shadow-lg',
    headMb: compact ? 'mb-1' : 'mb-2',
    iconBtn: compact ? 'w-6 h-6 min-w-[24px] min-h-[24px]' : 'w-[32px] h-[32px] min-w-[32px] min-h-[32px]',
    iconSize: compact ? 20 : 32,
    title: compact ? 'text-[12px]' : 'text-[14px]',
    sub: compact ? 'text-[10px]' : 'text-xs',
    date: compact ? 'text-[8px]' : 'text-[9px]',
    cell: compact ? 'p-1' : 'p-2',
    val: compact ? 'text-xs' : 'text-sm',
  };

  const symbol = examTypeSymbol(test.exam_type);
  const count = test.length || ((test.correct_count || 0) + (test.wrong_count || 0));
  const rate = count > 0 ? `${Math.round(((test.correct_count || 0) / count) * 100)}%` : '-';

  const handleIconClick = (e) => {
    e.stopPropagation(); // 카드 클릭 이벤트 전파 방지

    // 필요한 정보가 있는지 확인
    const student_id = studentId || test.student_id;

    if (student_id && test.stamp_id && test.exam_id) {
      navigate(`/student/${student_id}/stamp/${test.stamp_id}/result/${test.exam_id}`);
    } else {
      console.error('필요한 정보가 부족합니다:', {
        student_id,
        stamp_id: test.stamp_id,
        exam_id: test.exam_id
      });
    }
  };

  return (
    <div
      ref={ref}
      className={`cursor-pointer border bg-white ${c.round} ${c.pad} ${c.shadow} transition-all duration-300 hover:shadow-2xl hover:scale-[1.02] hover:border-primary/20
        ${isSelected ? 'border-transparent ring-2 ring-primary' : 'border-gray-200'}`}
      onClick={onClick}
    >
      {/* 헤더 (원본과 동일: 상태 아이콘 + 과목명/부제 + 생성/수정일) */}
      <div>
        <div className={`${c.headMb} flex items-center justify-between gap-2`}>
          <div className="flex min-w-0 items-center gap-2">
            {/* 상태 아이콘 */}
            <button
              onClick={handleIconClick}
              className={`${c.iconBtn} flex items-center justify-center rounded-full transition-colors hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-primary/20`}
              title="결과 상세보기"
            >
              {test.exam_status === 999 ? (
                <CheckCircle2 className="text-green-600" size={c.iconSize} />
              ) : test.exam_status === 400 ? (
                <PlayCircle className="text-orange-500" size={c.iconSize} />
              ) : (
                <FilePlus className="text-blue-500" size={c.iconSize} />
              )}
            </button>
            <div className="min-w-0">
              <h3 className={`${c.title} truncate font-semibold`}>{test.tree_name}</h3>
              <p className={`${c.sub} truncate text-gray-500`}>{test.edu_ver}</p>
            </div>
          </div>
          <div className={`shrink-0 whitespace-nowrap rounded-3xl ${c.date} font-medium text-gray-500`}>
            <div>생성: <V2DateTime value={test.created_at || test.exam_created_at} format="dateTimeDotSeconds" fallback="-" /></div>
            <div>수정: <V2DateTime value={test.updated_at || test.exam_updated_at} format="dateTimeDotSeconds" fallback="-" /></div>
          </div>
        </div>
      </div>

      <div>
        {collapsed ? (
          /* 축소: 4열 grid → 한 줄 텍스트 (TID·난이도·문제수·정답률, 브레이크다운 제외) */
          <div className="flex items-center justify-between whitespace-nowrap text-[10px] text-gray-500">
            <span>
              <span className="text-gray-400">TID </span>
              <span className="font-semibold text-gray-800">{test.exam_id}</span>
              <span className="ml-0.5 font-medium text-green-600">{symbol}</span>
            </span>
            <span>
              <span className="text-gray-400">난이도 </span>
              <span className="font-semibold text-gray-800">{test.difficulty || '-'}</span>
            </span>
            <span>
              <span className="text-gray-400">문제수 </span>
              <span className="font-semibold text-gray-800">{count}</span>
            </span>
            <span>
              <span className="text-gray-400">정답률 </span>
              <span className="font-semibold text-green-600">{rate}</span>
            </span>
          </div>
        ) : (
          /* 테스트 정보 (원본 4열 grid) */
          <div className="grid grid-cols-4 gap-1 text-xs">
            <div className={`rounded-3xl bg-gray-50 ${c.cell} text-center`}>
              <div className="text-gray-500">TID</div>
              <div className="flex flex-col items-center">
                <div className="flex items-center justify-center gap-1">
                  <div className={`${c.val} font-semibold text-gray-800`}>{test.exam_id}</div>
                  <span className={`${c.val} font-medium text-green-600`}>{symbol}</span>
                </div>
                {(test.exam_round !== undefined && test.exam_round !== null) && (
                  <div className="text-xs text-gray-500">({test.exam_round}회)</div>
                )}
              </div>
            </div>
            <div className={`rounded-3xl bg-gray-50 ${c.cell} text-center`}>
              <div className="text-gray-500">난이도</div>
              <div className="flex flex-col items-center justify-center">
                <div className={`${c.val} font-semibold text-gray-800`}>
                  {test.difficulty || '-'}
                </div>
              </div>
            </div>
            <div className={`rounded-3xl bg-gray-50 ${c.cell} text-center`}>
              <div className="text-gray-500">문제수</div>
              <div className="flex flex-col items-center justify-center">
                <div className={`${c.val} font-semibold text-gray-800`}>{count}</div>
              </div>
            </div>
            <div className={`rounded-3xl bg-gray-50 ${c.cell} text-center`}>
              <div className="text-gray-500">정답률</div>
              <div className="flex flex-col items-center">
                <div className={`${c.val} font-semibold text-green-600`}>{rate}</div>
                <div className="text-[8px] font-normal">
                  <span className="text-gray-400">(</span>
                  <span className="text-primary font-bold">{test.correct_count || 0}</span>
                  <span className="text-gray-400">/</span>
                  <span className="text-danger font-bold">{test.wrong_count || 0}</span>
                  <span className="text-gray-400">/</span>
                  <span className="text-black font-bold">{count}</span>
                  <span className="text-gray-400">)</span>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
});

HubTestHistoryItem.displayName = 'HubTestHistoryItem';

export default HubTestHistoryItem;
